/**
 * Product Gallery Scroll Navigation - Khaite Style
 * Implements smooth scroll through all images with sticky behavior
 * All images are visible and scroll continuously, no "blink" effect
 */

class ProductGalleryScroll {
  constructor() {
    this.mediaGallery = document.querySelector('media-gallery');
    if (!this.mediaGallery) return;

    this.viewer = this.mediaGallery.querySelector('[id^="GalleryViewer"]');
    this.mediaList = this.mediaGallery.querySelector('.product__media-list');
    this.productMediaWrapper = document.querySelector('.product__media-wrapper');
    this.productInfo = document.querySelector('.product__info-wrapper');
    this.mediaItems = Array.from(this.mediaGallery.querySelectorAll('.product__media-item'));
    this.header = document.querySelector('.header-wrapper');

    if (!this.mediaItems.length || this.mediaItems.length <= 1) {
      // Only one image, no need for scroll navigation
      return;
    }

    this.isSticky = false;
    this.hasReachedBottom = false;
    this.galleryScrollTop = 0;
    this.maxGalleryScroll = 0;
    this.headerHeight = 0;

    this.init();
  }

  init() {
    // Only enable on desktop
    if (window.innerWidth < 1025) return;

    // Calculate header height
    this.calculateHeaderHeight();

    // Calculate max scroll for gallery
    this.calculateMaxScroll();

    // Listen for window resize to recalculate
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 1025) {
        this.calculateHeaderHeight();
        this.calculateMaxScroll();
      }
    });

    // Listen for scroll events
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });

    // Listen for wheel events for smooth scrolling through images
    window.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });
  }

  calculateHeaderHeight() {
    if (this.header) {
      this.headerHeight = this.header.offsetHeight;
    } else {
      this.headerHeight = 0;
    }
  }

  calculateMaxScroll() {
    if (!this.mediaList) return;

    // Calculate the total height of all images
    let totalHeight = 0;
    this.mediaItems.forEach(item => {
      totalHeight += item.offsetHeight;
    });

    // Max scroll is total height minus viewport height (accounting for header)
    const viewportHeight = window.innerHeight - this.headerHeight;
    this.maxGalleryScroll = Math.max(0, totalHeight - viewportHeight);
  }

  handleScroll() {
    this.updateStickyState();
  }

  updateStickyState() {
    if (!this.productMediaWrapper) return;

    const rect = this.productMediaWrapper.getBoundingClientRect();
    const stickyThreshold = this.headerHeight;

    // Check if we should be sticky
    if (rect.top <= stickyThreshold && !this.hasReachedBottom) {
      this.makeSticky();
    } else if (this.hasReachedBottom || rect.top > stickyThreshold) {
      this.removeSticky();
    }
  }

  makeSticky() {
    if (!this.productMediaWrapper || this.isSticky) return;

    const topPosition = `${this.headerHeight}px`;

    this.productMediaWrapper.style.position = 'sticky';
    this.productMediaWrapper.style.top = topPosition;

    // Also make product info sticky
    if (this.productInfo) {
      this.productInfo.style.position = 'sticky';
      this.productInfo.style.top = topPosition;
    }

    this.isSticky = true;
  }

  removeSticky() {
    if (!this.productMediaWrapper || !this.isSticky) return;

    this.productMediaWrapper.style.position = 'relative';
    this.productMediaWrapper.style.top = 'auto';

    // Also remove sticky from product info
    if (this.productInfo) {
      this.productInfo.style.position = 'static';
      this.productInfo.style.top = 'auto';
    }

    this.isSticky = false;
  }

  handleWheel(event) {
    // Only run on desktop
    if (window.innerWidth < 1025) return;

    if (!this.productMediaWrapper || !this.mediaGallery || !this.mediaList) return;

    const galleryRect = this.mediaGallery.getBoundingClientRect();

    // Check if cursor is over the gallery area
    const isOverGallery = event.clientX >= galleryRect.left &&
                          event.clientX <= galleryRect.right &&
                          event.clientY >= galleryRect.top &&
                          event.clientY <= galleryRect.bottom;

    if (!isOverGallery) return;

    // Only intercept scroll when gallery is sticky
    if (!this.isSticky && galleryRect.top > this.headerHeight) return;

    const scrollDelta = event.deltaY;

    if (scrollDelta > 0) {
      // Scrolling down
      if (this.galleryScrollTop < this.maxGalleryScroll) {
        // Still scrolling through images
        event.preventDefault();
        this.galleryScrollTop = Math.min(this.galleryScrollTop + scrollDelta, this.maxGalleryScroll);
        this.scrollGallery();

        if (!this.isSticky) {
          this.makeSticky();
        }
      } else if (!this.hasReachedBottom) {
        // Just reached bottom of gallery, prepare to release scroll
        event.preventDefault();
        this.hasReachedBottom = true;

        // Small delay before releasing sticky
        setTimeout(() => {
          this.removeSticky();
        }, 100);
      }
      // If already past bottom, allow normal scroll
    } else if (scrollDelta < 0) {
      // Scrolling up
      if (this.hasReachedBottom && this.galleryScrollTop >= this.maxGalleryScroll) {
        // Coming back from released state
        event.preventDefault();
        this.hasReachedBottom = false;
        this.makeSticky();
      } else if (this.galleryScrollTop > 0) {
        // Scroll up through images
        event.preventDefault();
        this.galleryScrollTop = Math.max(this.galleryScrollTop + scrollDelta, 0);
        this.scrollGallery();

        if (!this.isSticky) {
          this.makeSticky();
        }
      }
    }
  }

  scrollGallery() {
    if (!this.mediaList) return;

    // Apply smooth scroll transform to the gallery
    this.mediaList.style.transform = `translateY(-${this.galleryScrollTop}px)`;
    this.mediaList.style.transition = 'transform 0.1s ease-out';
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductGalleryScroll();
  });
} else {
  new ProductGalleryScroll();
}
